import { FunctionComponent, ReactNode } from 'react';
import { Box, Typography } from '@material-ui/core';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import DateSelectorSection from './DateSelectorSection';
import { Moment } from 'moment-timezone';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons';
import { NewBookingAlert } from '@spike/new-booking-model';
import { AlertNewBooking } from '../UI/AlertNewBooking';
import clsx from 'clsx';

interface Props {
    isCollectDeposit?: boolean;
    date: Moment;
    slotTimes?: Array<string>;
    alerts?: Array<NewBookingAlert>;
    readOnly?: boolean;
    multiplePet?: boolean;
    withoutBorder?: boolean;
    withWarning?: boolean;
    showAlertBusinessHour?: boolean;
    disabled?: boolean;
    flow?: 'daycare' | 'boarding' | 'grooming';
    recurringAction?: ReactNode;
    onChangeDate: (date: Moment) => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            borderBottom: '1px solid #D4D4D4'
        },
        recurringActionContainer: {
            display: 'flex',
            alignItems: 'end',
            paddingBottom: '27px',
            [theme.breakpoints.up('md')]: {
                paddingBottom: '29px'
            }
        },
        alert: {
            marginBottom: 18,
            [theme.breakpoints.up('md')]: {
                marginBottom: 20
            },
            padding: '11px 15px !important'
        },
        alertText: {
            fontSize: 14,
            lineHeight: '20px'
        },
        dateSelectorContainer: {
            display: 'flex',
            flexDirection: 'column',
            [theme.breakpoints.up('md')]: {
                flexDirection: 'row'
            }
        },
        dateSelectorContainerWithAction: {
            flexWrap: 'wrap',
            columnGap: 17,
            flexDirection: 'row',
            [theme.breakpoints.up('md')]: {
                columnGap: 0
            }
        },
        withoutBorder: {
            borderBottom: 'none !important'
        }
    })
);

export const DateSelector: FunctionComponent<Props> = props => {
    const classes = useStyles();

    return (
        <Box
            className={clsx(classes.container, {
                [classes.withoutBorder]: props.withoutBorder
            })}
        >
            <Box
                className={clsx(classes.dateSelectorContainer, {
                    [classes.dateSelectorContainerWithAction]: !!props.recurringAction
                })}
            >
                <DateSelectorSection
                    date={props.date}
                    slotTimes={props.slotTimes}
                    withWarning={props.withWarning}
                    multiplePet={props.multiplePet}
                    disabled={props.disabled}
                    onSelectDate={props.onChangeDate}
                />
                {props.recurringAction && (
                    <Box className={classes.recurringActionContainer}>{props.recurringAction}</Box>
                )}
            </Box>
            {props.alerts?.map(alert => {
                if (alert.alertType !== 'date_time') {
                    return (
                        <Box
                            key={`booking_div_${alert.alertType}_alert`}
                            id={`booking_div_${alert.alertType}_alert`}
                        >
                            <AlertNewBooking
                                className={classes.alert}
                                bWidth="2px"
                                icon={faExclamationTriangle}
                                iconSize="lg"
                                iconColor="#EAB464"
                                bgColor="#F8F5F1"
                                bdColor="#BCB8AE"
                            >
                                <Typography className={classes.alertText}>{alert.message}</Typography>
                            </AlertNewBooking>
                        </Box>
                    );
                }
            })}
            {props.showAlertBusinessHour && (
                <Box
                    key="booking_div_date_time_alert"
                    id="booking_div_date_time_alert"
                >
                    <AlertNewBooking
                        className={classes.alert}
                        bWidth="2px"
                        icon={faExclamationTriangle}
                        iconSize="lg"
                        iconColor="#EAB464"
                        bgColor="#F8F5F1"
                        bdColor="#BCB8AE"
                    >
                        <Typography className={classes.alertText}>Date/Time outside business hours</Typography>
                    </AlertNewBooking>
                </Box>
            )}
        </Box>
    );
};

export default DateSelector;
